/**
 * Pickup waiting charge, as the rider sees it. Pure functions — no React, no
 * Firebase — so every rule is unit-tested.
 *
 * The SERVER decides everything that costs money (firebase/functions/waitingCharge.js):
 * it starts the clock only with proof the Anna is at the pickup, and settles the
 * charge when the rider's PIN is accepted. `computeWaitingCharge` mirrors that
 * maths exactly, so the live "₹x so far" never disagrees with what is finally
 * added. It is an estimate; once the trip starts, `ride.waitingCharge` is the truth.
 */
import { toMillis } from './rideData.js';

const MINUTE_MS = 60 * 1000;
/** Same sanity ceiling as the server. */
export const MAX_WAITING_MINUTES = 24 * 60;

const whole = (value) => {
  if (value == null || value === '') return 0;
  const n = Math.round(Number(value));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * The terms from a quote's `waiting` object, or null when there is nothing to
 * show or accept (feature off / quiet, or a ₹0 rate or cap). Never invents terms.
 */
export const waitingTermsFromQuote = (waiting) => {
  if (!waiting || typeof waiting !== 'object') return null;
  const termsId = typeof waiting.termsId === 'string' ? waiting.termsId.trim() : '';
  const freeMinutes = whole(waiting.freeMinutes);
  const perMinute = whole(waiting.perMinute);
  const maxCharge = whole(waiting.maxCharge);
  if (!termsId || perMinute <= 0 || maxCharge <= 0) return null;
  return { termsId, freeMinutes, perMinute, maxCharge };
};

/** True only if a charge could ever apply. */
export const isChargeable = (policy) => whole(policy?.perMinute) > 0 && whole(policy?.maxCharge) > 0;

/**
 * Each started minute counts, so 3:00 is still free and 3:01 is billed as the 4th
 * minute. Whole rupees; a ₹0 rate or ₹0 cap never charges.
 */
export const computeWaitingCharge = (policy, startedAtMs, nowMs) => {
  const start = Number(startedAtMs);
  if (!Number.isFinite(start) || start <= 0) return { minutes: 0, billableMinutes: 0, charge: 0 };
  const free = whole(policy?.freeMinutes);
  const rate = whole(policy?.perMinute);
  const cap = whole(policy?.maxCharge);
  const elapsedMs = Math.max(0, Number(nowMs) - start);
  const minutes = Math.min(MAX_WAITING_MINUTES, Math.ceil(elapsedMs / MINUTE_MS));
  const billableMinutes = Math.max(0, minutes - free);
  return { minutes, billableMinutes, charge: Math.min(cap, billableMinutes * rate) };
};

/**
 * Show the live clock only when the server accepted this Anna's arrival as proof:
 * the ride is `arrived`, carries the terms the rider accepted, and the clock belongs
 * to the Anna now on the ride. No proof (or quiet mode, which saves no terms) means
 * no clock — and also no charge, so the rider's screen and the bill always agree.
 */
export const isClockRunning = (ride) => Boolean(
  ride
  && ride.status === 'arrived'
  && isChargeable(ride.waitingPolicy)
  && toMillis(ride.waitingStartedAt) > 0
  && ride.waitingDriverId
  && ride.waitingDriverId === ride.driverId,
);

/** The live clock for `nowMs`, or null when it should not be shown. */
export const waitingClock = (ride, nowMs) => {
  if (!isClockRunning(ride)) return null;
  const startedAt = toMillis(ride.waitingStartedAt);
  const elapsedMs = Math.max(0, nowMs - startedAt);
  const freeMs = whole(ride.waitingPolicy.freeMinutes) * MINUTE_MS;
  const { minutes, charge } = computeWaitingCharge(ride.waitingPolicy, startedAt, nowMs);
  return {
    elapsedMs,
    freeRemainingMs: Math.max(0, freeMs - elapsedMs),
    charging: charge > 0 || elapsedMs > freeMs,
    chargeSoFar: charge,
    minutes,
  };
};

/** m:ss for a duration, e.g. 125000 → "2:05". Never negative. */
export const formatClock = (ms) => {
  const totalSeconds = Math.floor(Math.max(0, Number(ms) || 0) / 1000);
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
};
